/**
 * The send-back that teaches the author (plan v21 B5, docs/plans/2026-10-13_import-author-knows-the-plant.md).
 *
 * Beside the note, the reviewer answers each of the draft's four-part questions they want to: its assumption stands
 * (keep), or it is corrected, with what is right instead. The answers go with the rejection, are stored on that version
 * (review_answers), and the author reads them on its next round; its next draft says what it did with each instruction
 * and correction (author_notes.review_response), which the review then shows.
 *
 * A question is known by its place in the draft's author_notes.structured_questions (DraftQuestion.index), which the
 * server checks: a version's notes never change once stored.
 */
import type { ImportExtractorVersion, ImportReviewAnswer } from "@/lib/api/imports";
import { own } from "./own";

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** The reviewer's answer to one question: its assumption stands, or a correction (the text may still be empty). */
export type AnswerChoice = { keep: true } | { keep: false; text: string };

/** The reviewer's answers, by the question's place in the draft's structured_questions. */
export type Answers = Readonly<Record<number, AnswerChoice>>;

/** The most a correction may say, in characters, as the server takes it. */
export const CORRECTION_MAX = 1000;

/** A correction as one line: its line breaks, tabs and runs of spaces folded to single spaces, as the server stores it. */
function folded(text: string): string {
  return text.split(/\s+/).filter(Boolean).join(" ");
}

/** The answers as the rejection sends them: in question order, a correction's text folded to one line. */
export function answersBody(answers: Answers): ImportReviewAnswer[] {
  return Object.keys(answers)
    .map(Number)
    .filter((i) => Number.isInteger(i) && i >= 0)
    .sort((a, b) => a - b)
    .map((index) => {
      const a = answers[index];
      return a.keep ? { index, keep: true } : { index, keep: false, text: folded(a.text) };
    });
}

/** How many answers correct an assumption with something written. */
export function corrections(answers: Answers): number {
  return answersBody(answers).filter((a) => !a.keep && a.text).length;
}

/** Whether the draft can be sent back, and if not, why: a correction must say what is right, and a send-back says why
 *  (a note, or at least one correction), as the server asks. `blankCorrection`: a correction was started and left
 *  empty, which the footer says (a review not yet answered at all is not nagged). */
export interface SendBackState {
  ok: boolean;
  reason: string;
  blankCorrection: boolean;
}

export function sendBackState(note: string, answers: Answers): SendBackState {
  if (answersBody(answers).some((a) => !a.keep && !a.text)) {
    return { ok: false, reason: "Write each correction, or keep that assumption.", blankCorrection: true };
  }
  if (!note.trim() && corrections(answers) === 0) {
    return { ok: false, reason: "Say what to change, or correct one of its assumptions.", blankCorrection: false };
  }
  return { ok: true, reason: "", blankCorrection: false };
}

/** What a send-back carried, for its toast: the note, the corrections, or both. */
export function sentWith(note: string, answers: Answers): string {
  const n = corrections(answers);
  const fixes = n === 1 ? "your correction" : `your ${n} corrections`;
  if (note.trim() && n > 0) return `your note and ${fixes}`;
  if (n > 0) return fixes;
  return "your note";
}

/** One of the draft's four-part questions as the review lists it, with its place in structured_questions. */
interface AskedQuestion {
  saw: string;
  assume: string;
}

function askedQuestions(notes: unknown): AskedQuestion[] {
  const raw = isObj(notes) ? notes.structured_questions : undefined;
  return (Array.isArray(raw) ? raw : []).map((q) => (isObj(q) ? { saw: text(q.saw), assume: text(q.assume) } : { saw: "", assume: "" }));
}

/** An answer the reviewer gave when a version was sent back, with the question it answered. */
export interface GivenAnswer {
  /** What the author saw, and what it assumed: the question, in words. */
  saw: string;
  assume: string;
  kept: boolean;
  /** The correction, when not kept. */
  correction: string;
}

/** The answers given when `version` was sent back (its review_answers), each with its question from its own notes. An
 *  answer whose question the notes do not hold (an older version) is said without it. */
export function givenAnswers(version: Pick<ImportExtractorVersion, "review_answers" | "author_notes"> | null | undefined): GivenAnswer[] {
  if (!version) return [];
  const asked = askedQuestions(version.author_notes);
  const out: GivenAnswer[] = [];
  for (const raw of Array.isArray(version.review_answers) ? version.review_answers : []) {
    if (!isObj(raw)) continue;
    const index = typeof raw.index === "number" ? raw.index : -1;
    const q = index >= 0 && index < asked.length ? asked[index] : { saw: "", assume: "" };
    out.push({ saw: q.saw, assume: q.assume, kept: raw.keep === true, correction: raw.keep === true ? "" : text(raw.text) });
  }
  return out;
}

/** One line of the author's answer to a send-back: the instruction or correction, done or not, and how (or why not). */
export interface ResponseLine {
  item: string;
  done: boolean;
  how: string;
}

/** The author's answer to the send-back before it (author_notes.review_response), as written; none from a draft that
 *  answers nothing (a first draft, or a brain from before B5). */
export function reviewResponseOf(notes: unknown): ResponseLine[] {
  const raw = isObj(notes) ? own(notes, "review_response") : undefined;
  const out: ResponseLine[] = [];
  for (const r of Array.isArray(raw) ? raw : []) {
    if (!isObj(r)) continue;
    const item = text(r.item);
    if (!item) continue;
    out.push({ item, done: r.done === true, how: text(r.how) });
  }
  return out;
}
